import useSWR from 'swr';
import { useSearchParams } from 'next/navigation';
import { fetchWithTracingAndLogging } from '@/app/components/hotel-detail/common/fetchWithTracing';
import { getLoggingInfo } from '@/app/components/hotel-detail/lib/loggingInfo';
import { BASE_PATH } from '@/app/components/search/utils/path';
import { useInitialParams } from '@/app/components/hotel-detail/InitialParamsProvider';
import {
  PRICE_CALENDAR_API_DATE_FORMAT,
  URL_DATE_FORMAT,
  getDateRangeExcludingPastDates,
} from '@/app/components/hotel-detail/common/dates';
import { ADULTS_DEFAULT, URLParameters } from '@/app/common/urlParameters';
import { usePageWrapperData } from '@/app/common/pageWrapperProvider.tsx';
import { useUrlSearchParams } from '../UrlSearchParamsProvider';
import { isValidHotelId } from '../common/common.helper.ts';

export function useAlternativeDates() {
  const { pageType, internalHotelId } = useInitialParams();
  const { searchInfoData, commonData, localization } = usePageWrapperData();
  const { accommodationOnly, checkin, nights } = useUrlSearchParams();

  const searchParams = useSearchParams();
  const origin = searchParams.get(URLParameters.ORIGIN);

  const priceCalendarDates = checkin
    ? getDateRangeExcludingPastDates(checkin, URL_DATE_FORMAT, PRICE_CALENDAR_API_DATE_FORMAT, 14, 14)
    : undefined;

  return useSWR<AlternativeDatesResponse | null>(
    // The API does not support more than 14 nights
    priceCalendarDates && nights < 15 && isValidHotelId(internalHotelId)
      ? `alternative-dates-${priceCalendarDates.from}-${priceCalendarDates.to}`
      : undefined,
    async () => {
      const payloadData: PriceCalendarPayload = {
        accommodationOnly,
        currency: localization.currency,
        from: priceCalendarDates?.from || '',
        internalHotelId,
        locale: localization.locale,
        logged: commonData.authResponse.loggedIn,
        nights: getNightsRange(nights),
        adults: searchParams.get(URLParameters.ADULTS) || ADULTS_DEFAULT.toString(),
        childrenAges: searchParams.get(URLParameters.AGES) || '',
        page: pageType,
        to: priceCalendarDates?.to || '',
        origin: origin || '',
        taxesIncluded: localization.country === 'GB' && accommodationOnly,
      };
      return fetchWithTracingAndLogging<AlternativeDatesResponse>(
        `${BASE_PATH}/api/hotel-detail/alternativeDates`,
        {
          headers: { 'content-type': 'application/json' },
          method: 'POST',
          body: JSON.stringify(payloadData),
        },
        getLoggingInfo(pageType, 'priceCalendarHotel', 'priceCalendarApiRoute'),
        searchInfoData.searchTraceParent,
        commonData
      );
    },
    {
      revalidateIfStale: false,
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      keepPreviousData: true,
    }
  );
}

// Calculate nights range for API - provides +/- 1 night variation for flexibility
const getNightsRange = (nights: number): string => {
  if (nights <= 1) return '1,2';
  if (nights >= 14) return '13,14'; // Max supported by API
  return `${nights - 1},${nights + 1}`;
};
